import type { IRiskImpactDimension, IRiskThreat, IRiskVulnerability } from '@/lib/constants/riskThreatsVulnerabilities'
import { RISK_IMPACT_DIMENSIONS } from '@/lib/constants/riskThreatsVulnerabilities'

export const RISK_THREAT_NAMES = {
  TC01: 'Error in use',
  TC02: 'Abuse of rights or permissions',
  TC03: 'Forging of rights or permissions',
  TC04: 'Denial of actions',
  TH01: 'Terror attack, sabotage',
  TH02: 'Social engineering',
  TH04: 'Remote spying',
  TH05: 'Eavesdropping',
  TH06: 'Theft of media or documents',
  TH07: 'Theft of equipment',
  TH08: 'Theft of digital identity or credentials',
  TH10: 'Disclosure of information',
  TH11: 'Data input from untrustworthy sources',
  TH13: 'Tampering with software',
  TH14: 'Drive-by-exploits using web-based communication',
  TH15: 'Replay attack, man-in-the-middle attack',
  TH16: 'Unauthorized processing of personal data',
  TH17: 'Unauthorized entry to facilities',
  TH19: 'Incorrect use of devices',
  TH22: 'Use of counterfeit or copied software',
  TH23: 'Corruption of data',
  TH24: 'Illegal processing of data',
  TH25: 'Sending or distributing of malware',
  TI01: 'Failure of a supply system',
  TI03: 'Loss of power supply',
  TI04: 'Failure of a telecommunications network',
  TI05: 'Failure of telecommunication equipment',
  TN06: 'Pandemic/epidemic phenomenon',
  TO01: 'Lack of staff',
  TO02: 'Lack of resources',
  TO03: 'Failure of service providers',
  TO04: 'Violation of laws or regulations',
  TP01: 'Fire',
  TP02: 'Water',
  TQ01: 'Cloud provider account compromise',
  TQ02: 'Cloud misconfiguration (S3, IAM, security groups)',
  TQ03: 'Container/serverless vulnerability exploitation',
  TQ04: 'SaaS supply chain attack',
  TQ05: 'API security breach',
  TQ06: 'Multi-tenant data leakage',
  TQ07: 'Serverless function injection',
  TQ08: 'CI/CD pipeline compromise',
  TQ09: 'Shadow IT and unmanaged SaaS proliferation',
  TQ10: 'Rapid scaling without security review',
  TQ11: 'Key person dependency (bus factor)',
  TQ12: 'Insecure remote work practices',
  TQ13: 'Insufficient offboarding of departing employees',
  TQ15: 'GDPR/LOPDGDD non-compliance',
  TQ16: 'AEPD enforcement action',
  TQ17: 'Cross-border data transfer violation',
  TQ20: 'Lack of cookie/consent management',
  TQ21: 'Lack of incident response plan',
  TQ22: 'Lack of formal security onboarding process',
  TQ23: 'Loss or theft of portable devices',
  TQ24: 'Hardware malfunction or end-of-life failure without a replacement/maintenance plan',
  TQ26: 'AI model manipulation: data poisoning, prompt injection or adversarial inputs',
  TQ27: 'AI model failure: hallucination, bias or performance drift undermining output reliability',
  TT01: 'Failure of device or system',
  TT02: 'Saturation of the information system',
  TT03: 'Violation of information system maintainability',
} satisfies Record<IRiskThreat, string>

export const RISK_VULNERABILITY_NAMES = {
  VH05: 'Lack of efficient configuration change control',
  VH08: 'Unprotected storage',
  VH11: 'Lack of mobile device management or remote wipe',
  VH12: 'Lack of full-disk encryption',
  VH13: 'Aging or end-of-life hardware without a replacement plan',
  VN01: 'Lack of proof of sending or receiving a message',
  VN02: 'Unprotected communication lines',
  VN03: 'Unprotected sensitive traffic',
  VN05: 'Single point of failure',
  VN06: 'Lack of identification and authentication of sender and receiver',
  VN07: 'Insecure network architecture',
  VN08: 'Transfer of passwords in clear',
  VN09: 'Inadequate network management (resilience of routing)',
  VN10: 'Unprotected public network connections',
  VO01: 'Lack of formal procedure for user registration and de-registration',
  VO02: 'Lack of formal process for access right review',
  VO03: 'Lack or insufficient security provisions in contracts with customers or third parties',
  VO04: 'Lack of procedure for monitoring information processing facilities',
  VO05: 'Lack of regular audits',
  VO06: 'Lack of procedures for risk identification and assessment',
  VO09: 'Lack or insufficient service level agreement',
  VO13: 'Lack of formal process for authorizing publicly available information',
  VO14: 'Lack of proper allocation of information security responsibilities',
  VO15: 'Lack of continuity plans',
  VO17: 'Lack of procedures for introducing software into operational systems',
  VO18: 'Lack of procedures for handling classified information',
  VO26: 'Lack of established monitoring mechanisms for security breaches',
  VO28: 'Lack of procedures for compliance with intellectual property rights',
  VP01: 'Absence of personnel',
  VP03: 'Insufficient security training',
  VP04: 'Incorrect use of software and hardware',
  VP05: 'Lack of security awareness',
  VP07: 'Unsupervised work by outside or cleaning staff',
  VQ01: 'Lack of cloud security posture management (CSPM)',
  VQ02: 'Overly permissive IAM roles',
  VQ03: 'Insufficient tenant isolation',
  VQ04: 'Insufficient cloud logging',
  VQ05: 'Lack of infrastructure as code (IaC) scanning',
  VQ06: 'Publicly accessible cloud storage',
  VQ07: 'Lack of VPC segmentation',
  VQ08: 'Lack of multi-factor authentication (MFA)',
  VQ09: 'Lack of login anomaly alerts',
  VQ10: 'Hardcoded secrets in source code',
  VQ11: 'Lack of formal security onboarding and offboarding',
  VQ12: 'Lack of knowledge transfer',
  VQ14: 'Weak endpoint protection',
  VQ15: 'Lack of data classification',
  VQ16: 'Lack of incident response plan',
  VQ17: 'Lack of vendor security assessment',
  VQ19: 'Lack of data protection governance (DPO, DPIA)',
  VQ20: 'Incomplete record of processing activities',
  VQ21: 'Lack of a compliant cookie banner',
  VQ22: 'Lack of consent management',
  VQ23: 'Lack of breach notification procedure',
  VQ24: 'Lack of data processing agreements with sub-processors',
  VQ25: 'Lack of safeguards for international data transfers (SCCs)',
  VQ26: 'Lack of AI input validation and adversarial testing',
  VQ27: 'Poorly governed AI training data',
  VS01: 'No or insufficient software testing',
  VS02: 'Well-known flaws in the software',
  VS04: 'Disposal or reuse of storage media without proper erasure',
  VS05: 'Lack of audit trail',
  VS06: 'Wrong allocation of access rights',
  VS09: 'Complicated user interface',
  VS11: 'Incorrect parameter setup',
  VS13: 'Lack of identification and authentication mechanisms',
  VS15: 'Poor password management',
  VS16: 'Unnecessary services enabled',
  VS18: 'Unclear or incomplete specifications for developers',
  VS19: 'Lack of effective change control',
  VS20: 'Uncontrolled downloading and use of software',
  VS21: 'Lack of backup copies',
  VS22: 'Failure to produce management reports',
  VS_S01: 'Inadequate or careless use of physical access control to buildings and rooms',
  VS_S03: 'Unstable power grid',
  VS_S04: 'Lack of physical protection of the building, doors and windows',
} satisfies Record<IRiskVulnerability, string>

export const RISK_THREATS_SELECTOR = {
  LABEL: 'Threats',
  EMPTY: 'No threat',
  SEARCH: 'Search threats',
  NO_RESULTS: 'No threats found',
  SELECT: 'Select threats...',
  REMOVE: 'Remove',
  SUGGESTIONS_INFO: 'Suggested from the affected assets',
  NO_SUGGESTIONS: 'Add affected assets to get suggestions',
}

export const RISK_VULNERABILITIES_SELECTOR = {
  LABEL: 'Vulnerabilities',
  EMPTY: 'No vulnerability',
  SEARCH: 'Search vulnerabilities',
  NO_RESULTS: 'No vulnerabilities found',
  SELECT: 'Select vulnerabilities...',
  REMOVE: 'Remove',
  SUGGESTIONS_INFO: 'Suggested from the selected threats',
  NO_SUGGESTIONS: 'Select threats to get suggestions',
}

export const RISK_IMPACT_DIMENSIONS_SELECTOR = {
  LABEL: 'Impact dimensions',
  DIMENSIONS: {
    [RISK_IMPACT_DIMENSIONS.AVAILABILITY]: { SHORT: 'A', LABEL: 'Availability' },
    [RISK_IMPACT_DIMENSIONS.CONFIDENTIALITY]: { SHORT: 'C', LABEL: 'Confidentiality' },
    [RISK_IMPACT_DIMENSIONS.INTEGRITY]: { SHORT: 'I', LABEL: 'Integrity' },
    [RISK_IMPACT_DIMENSIONS.AUTHENTICITY]: { SHORT: 'Au', LABEL: 'Authenticity' },
    [RISK_IMPACT_DIMENSIONS.TRACEABILITY]: { SHORT: 'T', LABEL: 'Traceability' },
  } satisfies Record<IRiskImpactDimension, { SHORT: string; LABEL: string }>,
}
